import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { loadPublicOfferBySlug } from '../services/firestoreSync';
import { PublicOfferDocument } from '../types';
import {
  normalizePublicOfferSlug,
  publicOfferToPreviewSponsors,
  publicOfferToPreviewState,
} from '../lib/publicOffers';
import { PublicOfferPreview } from './EventExperience';
import { GrosymLogo } from './GrosymLogo';

export const PublicOfferRoute: React.FC<{ slug: string }> = ({ slug: rawSlug }) => {
  const slug = normalizePublicOfferSlug(rawSlug);
  const [offer, setOffer] = useState<PublicOfferDocument | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadPublicOfferBySlug(slug).then((loaded) => {
      if (!active) return;
      setOffer(loaded);
      setLoading(false);
    });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!offer) return;
    document.title = `${offer.landingPage.headline} | Grosym`;
    let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.name = 'description';
      document.head.appendChild(description);
    }
    description.content = offer.landingPage.summary;
  }, [offer]);

  const previewState = useMemo(() => offer ? publicOfferToPreviewState(offer) : null, [offer]);
  const sponsors = useMemo(() => offer ? publicOfferToPreviewSponsors(offer) : [], [offer]);

  if (loading) {
    return <main className="min-h-screen bg-[#07111d] text-white flex flex-col items-center justify-center gap-4"><GrosymLogo className="h-12 w-12" /><Loader2 size={22} className="animate-spin text-emerald-300" /><p className="text-xs font-bold text-slate-400">Abriendo página…</p></main>;
  }

  if (!offer || !previewState) {
    return (
      <main className="min-h-screen bg-[#f6f8f7] px-5 flex items-center justify-center">
        <section className="w-full max-w-lg rounded-[2rem] border border-slate-200 bg-white p-8 text-center shadow-xl shadow-slate-200/60">
          <GrosymLogo className="mx-auto h-12 w-12" />
          <p className="mt-6 text-[10px] font-black uppercase tracking-[.18em] text-emerald-700">Grosym</p>
          <h1 className="mt-2 text-3xl font-black tracking-[-.04em] text-slate-950">Esta página no está disponible</h1>
          <p className="mt-3 text-sm leading-6 text-slate-500">Comprueba el enlace o pídele a quien la creó que vuelva a publicarla.</p>
          <a href="https://grosym.site" className="mx-auto mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-slate-950 px-6 text-xs font-black text-white"><ArrowLeft size={15} /> Ir a Grosym</a>
        </section>
      </main>
    );
  }

  return <PublicOfferPreview state={previewState} sponsors={sponsors} landing={offer.landingPage} standalone />;
};
